import { Injectable } from '@angular/core';

export interface DownloadOptions {
  filename?: string;
  data: any;
}

/**
 * Service for downloading data in various formats (JSON, CSV)
 *
 * Example usage:
 *
 * // Download JSON
 * downloadService.downloadAsJson({
 *   data: { name: 'John', age: 30 },
 *   filename: 'user-data.json'
 * });
 *
 * // Download CSV
 * downloadService.downloadAsCsv({
 *   data: { question1: 'Answer 1', question2: 'Answer 2' },
 *   filename: 'responses.csv'
 * });
 */
@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  /**
   * Download data as JSON file
   */
  downloadAsJson(options: DownloadOptions): void {
    const { data, filename = 'data.json' } = options;

    if (!data) {
      console.warn('No data provided for JSON download');
      return;
    }

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    this.downloadBlob(blob, filename);
  }

  /**
   * Download data as CSV file
   */
  downloadAsCsv(options: DownloadOptions): void {
    const { data, filename = 'data.csv' } = options;

    if (!data) {
      console.warn('No data provided for CSV download');
      return;
    }

    const csvContent = this.convertDataToCsv(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, filename);
  }

  /**
   * Convert response data to CSV format
   */
  private convertDataToCsv(data: any): string {
    const csvRows: string[] = [];

    // Create header row
    const headers = ['Question', 'Answer'];
    csvRows.push(headers.map((header) => `"${header}"`).join(','));

    // Export all data as key-value pairs
    Object.keys(data).forEach((key) => {
      const value = data[key];

      let formattedValue = '';
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          formattedValue = value.join('; ');
        } else if (typeof value === 'object') {
          formattedValue = JSON.stringify(value);
        } else {
          formattedValue = String(value);
        }
      }

      // Escape quotes in the data
      const escapedKey = key.replace(/"/g, '""');
      const escapedValue = formattedValue.replace(/"/g, '""');

      csvRows.push(`"${escapedKey}","${escapedValue}"`);
    });

    return csvRows.join('\n');
  }

  /**
   * Download blob as file
   */
  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
